import React from 'react';

import { Info } from 'lucide-react';

import type { LimitedRate, RateLimit } from '../../../api/pricingApprovals';
import type { PricingConfiguration } from '../../../types';

// v9 wizard step 6 (renderWizard bodies[6]).
const ROWS: { rate: LimitedRate; label: string; unit: '%' | '$' }[] = [
	{ rate: 'discount_rate', label: 'Discount rate (markup)', unit: '%' },
	{ rate: 'authorization_fee', label: 'Authorization fee', unit: '$' },
	{ rate: 'monthly_fee', label: 'Monthly service fee', unit: '$' },
	{ rate: 'batch_fee', label: 'Batch fee', unit: '$' },
];

const BOUNDS: { key: keyof RateLimit; label: string }[] = [
	{ key: 'floor', label: 'Floor' },
	{ key: 'ceiling', label: 'Ceiling' },
	{ key: 'rep_min', label: 'Rep floor' },
	{ key: 'rep_max', label: 'Rep ceiling' },
];

const show = (unit: '%' | '$', v: number | null | undefined) =>
	v == null ? '' : unit === '%' ? `${v.toFixed(2)}%` : `$${v.toFixed(2)}`;

const th =
	'bg-gray-50 px-3 py-2.5 text-left text-xs font-medium uppercase tracking-[.05em] text-gray-500 border-b border-gray-200 whitespace-nowrap first:pl-4';
const td =
	'px-3 py-2 text-sm text-gray-900 border-b border-gray-200 whitespace-nowrap first:pl-4';
const input =
	'block w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm leading-5 text-gray-900 tabular-nums focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500';

interface LimitsStepProps {
	configuration: PricingConfiguration;
	onUpdate: (updates: Partial<PricingConfiguration>) => void;
}

const LimitsStep: React.FC<LimitsStepProps> = ({ configuration, onUpdate }) => {
	const limits = configuration.pricing_limits ?? {};

	const setBound = (rate: LimitedRate, key: keyof RateLimit, raw: string) => {
		const text = raw.replace(/[$%\s]/g, '');
		const value = text === '' ? null : Number(text);
		if (value !== null && !Number.isFinite(value)) return;
		onUpdate({
			pricing_limits: { ...limits, [rate]: { ...limits[rate], [key]: value } },
		});
	};

	return (
		<div>
			<div className="mb-8 text-center">
				<h3 className="mb-3 text-2xl font-bold text-gray-900">Limits</h3>
				<p className="text-lg text-gray-600">
					Set the floor and ceiling per rate, the narrower rep range, and who
					approves anything outside
				</p>
			</div>
			<div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
				<table className="w-full table-fixed border-collapse">
					<colgroup>
						<col style={{ width: '26%' }} />
						<col style={{ width: '12%' }} />
						<col style={{ width: '12%' }} />
						<col style={{ width: '12%' }} />
						<col style={{ width: '12%' }} />
						<col style={{ width: '26%' }} />
					</colgroup>
					<thead>
						<tr>
							<th className={th}>Rate field</th>
							{BOUNDS.map((b) => (
								<th key={b.key} className={th}>
									{b.label}
								</th>
							))}
							<th className={th}>Approver</th>
						</tr>
					</thead>
					<tbody>
						{ROWS.map(({ rate, label, unit }) => (
							<tr key={rate}>
								<td className={td}>{label}</td>
								{BOUNDS.map((b) => (
									<td key={b.key} className={td}>
										<input
											// Remount when the stored value changes so the formatted text follows it.
											key={String(limits[rate]?.[b.key] ?? '')}
											type="text"
											inputMode="decimal"
											aria-label={`${label} ${b.label}`}
											defaultValue={show(unit, limits[rate]?.[b.key])}
											onBlur={(e) => setBound(rate, b.key, e.target.value)}
											className={input}
										/>
									</td>
								))}
								{/* Approver per rate is not stored yet: any admin approves. */}
								<td className={`${td} text-gray-500`}>—</td>
							</tr>
						))}
						<tr>
							<td className={td}>Deal margin floor</td>
							<td className={`${td} text-gray-500`}>—</td>
							<td className={`${td} text-gray-500`}>per month</td>
							<td className={td} />
							<td className={td} />
							<td className={`${td} text-gray-500`}>—</td>
						</tr>
					</tbody>
				</table>
			</div>
			<div className="mt-4 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
				<Info className="mt-px h-5 w-5 flex-none text-blue-600" />
				<div>
					Reps move rates inside the rep range without approval. The pricing
					team moves inside the floor and ceiling. Anything outside goes to the
					approver named here.
				</div>
			</div>
		</div>
	);
};

export default LimitsStep;
